import React, { useContext, useState } from "react";
import {
  Avatar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Theme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LogoutIcon from "@mui/icons-material/Logout";
import { LeaveSummary as LeaveSummaryType } from "../../types/types";
import { formatMoney } from "../../utils/formatMoney";
import dayjs from "dayjs";
import { LanguageContext } from "../../context/LanguageContext";
import { LocalizedStrings } from "react-localization";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const endingContract = (end_contract: string | null, strings: LocalizedStrings<any>) => {
  if (end_contract === null) return <></>;
  const six_months_ahead = dayjs().add(6, "month");
  const end_contract_date = dayjs(end_contract);
  if (end_contract_date.isBefore(six_months_ahead)) {
    return (
      <Tooltip
        arrow
        placement="top"
        title={`${strings.formatString(
          strings.leave.contractend,
          dayjs(end_contract_date).format("D-MM-YYYY")
        )}`}
      >
        <Avatar
          sx={(theme: Theme) => ({
            bgcolor: theme.palette.dark.main,
            width: "24px",
            height: "24px",
          })}
        >
          <LogoutIcon sx={{ fontSize: ".9rem", width: "11px", height: "12px" }} />
        </Avatar>
      </Tooltip>
    );
  } else {
    return <></>;
  }
};

const getStatusColor = (opname: number, period: number, end_contract: string | null, balance: number) => {
  if (balance <= 0) return "success";
  const percentage_period = (opname / period) * 100;
  if (end_contract !== null) {
    const end_contract_date = dayjs(end_contract);
    const six_months_ahead = dayjs().add(6, "month");
    if (end_contract_date.isBefore(six_months_ahead) && percentage_period < 100) return "error";
  }
  if (percentage_period >= 100) return "success";
  if (percentage_period >= 75) return "warning";
  return "error";
};

const LeaveDetails: React.FC<{ leave: LeaveSummaryType }> = ({ leave }) => {
  const { strings } = useContext(LanguageContext);
  const [page, setPage] = useState(0);
  const rowsPerPage = 7;

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const totalPages =
    Math.ceil(leave.details.length / rowsPerPage) > 0 ? Math.ceil(leave.details.length / rowsPerPage) : 1;

  const paginatedDetails = leave.details.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Box>
        <TableContainer sx={{ mb: 2 }}>
          <Table aria-label="leave details table" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  colSpan={7}
                  sx={{
                    pb: 2,
                    px: 0.5,
                    pt: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    pl: 0,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {strings.leave.name}
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{ pb: 2, px: 0.5, textAlign: "right", pt: 0, fontSize: "15px", fontWeight: "bold" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {strings.leave.rest}
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{ pb: 2, px: 0.5, textAlign: "right", pt: 0, fontSize: "15px", fontWeight: "bold" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {strings.leave.period}
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{ pb: 2, px: 0.5, textAlign: "right", pt: 0, fontSize: "15px", fontWeight: "bold" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {strings.leave.taken}
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{ pb: 2, px: 0.5, textAlign: "right", pt: 0, fontSize: "15px", fontWeight: "bold" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {strings.leave.balance}
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={2}
                  sx={{ pb: 2, px: 0.5, textAlign: "right", pt: 0, fontSize: "15px", fontWeight: "bold" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {strings.leave.value}
                  </Typography>
                </TableCell>
                <TableCell
                  colSpan={1}
                  sx={{
                    pb: 2,
                    px: 0.5,
                    textAlign: "right",
                    pt: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    pr: 0,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    &nbsp;
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDetails.map((detail) => (
                <TableRow key={detail.employee}>
                  <TableCell sx={{ px: 0.5, pl: 0 }} colSpan={7}>
                    <Stack direction={"row"} spacing={1} useFlexGap sx={{ alignItems: "center" }}>
                      <Typography
                        variant="body1"
                        component={"span"}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {detail.name}
                      </Typography>
                      {endingContract(detail.end_contract, strings)}
                    </Stack>
                  </TableCell>
                  <TableCell colSpan={2} sx={{ px: 0.5, textAlign: "right" }}>
                    {detail?.rest ? detail?.rest.toFixed(0) : 0}
                  </TableCell>
                  <TableCell colSpan={2} sx={{ px: 0.5, textAlign: "right" }}>
                    {detail?.period ? detail?.period.toFixed(0) : 0}
                  </TableCell>
                  <TableCell colSpan={2} sx={{ px: 0.5, textAlign: "right" }}>
                    {detail.taken.toFixed(0)}
                  </TableCell>
                  <TableCell colSpan={2} sx={{ px: 0.5, textAlign: "right" }}>
                    {detail.balance.toFixed(0)}
                  </TableCell>
                  <TableCell colSpan={2} sx={{ px: 0.5, textAlign: "right" }}>
                    {formatMoney.format(detail.value).split(",")[0] + ""}
                  </TableCell>
                  <TableCell colSpan={1} sx={{ px: 0.5, textAlign: "right", pr: 0 }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title={`${((detail.taken / detail.period) * 100).toFixed(1)}% opgenomen`}
                    >
                      <Avatar
                        sx={(theme: Theme) => ({
                          bgcolor:
                            theme.palette[
                              getStatusColor(detail.taken, detail.period, detail.end_contract, detail.balance)
                            ].main,
                          width: "24px",
                          height: "24px",
                          my: "3px",
                          ml: "auto",
                        })}
                      >
                        &nbsp;
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Stack
        direction="row"
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          mt: 2,
        }}
      >
        <Stack direction={"row"} mt={1} spacing={2} alignItems={"center"}>
          <Stack direction="row" alignItems="center">
            <Box
              sx={(theme: Theme) => ({
                width: 16,
                height: 16,
                bgcolor: theme.palette.success.main,
                mr: 1,
              })}
            />
            <Typography variant="body2">{`100%`}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box
              sx={(theme: Theme) => ({
                width: 16,
                height: 16,
                bgcolor: theme.palette.warning.main,
                mr: 1,
              })}
            />
            <Typography variant="body2">{`> 75%`}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box
              sx={(theme: Theme) => ({
                width: 16,
                height: 16,
                bgcolor: theme.palette.error.main,
                mr: 1,
              })}
            />
            <Typography variant="body2">{`< 75%`}</Typography>
          </Stack>
        </Stack>
        {totalPages !== 1 && (
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => handleChangePage(null, page - 1)} disabled={page === 0} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography component="span" sx={{ mx: 1 }}>
              {page + 1}/
              <Typography component={"span"} fontWeight={"bold"}>
                {totalPages}
              </Typography>
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= totalPages - 1}
              size="small"
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        )}
      </Stack>
    </>
  );
};

export default LeaveDetails;
